/**
 * Account API - Unified view of Advertisers + Clients
 * 
 * This module provides read-through composition of Advertiser and Client data
 * into a single "Account" concept for the UI.
 * 
 * Account ID = Advertiser ID
 * Client data (billing, contacts) is attached context, not identity.
 */

import { apiRequest } from '@/lib/queryClient';
import API_BASE from '@/lib/apiBase';

/**
 * Account represents the unified view of an Advertiser + optional Client data
 */
export interface Account {
    // Primary identity (from Advertiser)
    id: number;
    name: string;
    address: string | null;
    contactName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    campaignCount?: number;

    // Linked Client data (billing context)
    clientId: number | null;
    clientName: string | null;

    // Billing info (from Client if linked)
    billing?: {
        billingName: string | null;
        billingAddress: string | null;
        vatNumber: string | null;
        paymentTerms: string | null;
    } | null;

    // Primary contact from Client (if different from advertiser contact)
    clientContact?: {
        name: string | null;
        email: string | null;
        phone: string | null;
    } | null;
}

export interface AccountInput {
    name: string;
    address?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    clientId?: number | null;
}

interface AccountListResponse {
    success: boolean;
    data: {
        accounts: Account[];
        pagination: {
            limit: number;
            offset: number;
            total: number;
            hasMore: boolean;
        };
    };
    message: string;
}

interface AccountDetailResponse {
    success: boolean;
    data: {
        account: Account;
    };
    message: string;
}

/**
 * Fetch paginated list of accounts (advertisers with client enrichment)
 */
export async function fetchAccounts(
    search?: string,
    limit = 50,
    offset = 0
): Promise<Account[]> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    // Use existing advertisers endpoint - it already includes clientId and clientName
    const response = await apiRequest(`/api/admin/advertisers?${params.toString()}`, 'GET');
    const jsonData = await response.json();

    if (!jsonData?.data?.advertisers) {
        throw new Error('Failed to fetch accounts');
    }

    // Map advertisers to Account interface
    return jsonData.data.advertisers.map((adv: any) => ({
        id: adv.id,
        name: adv.name,
        address: adv.address,
        contactName: adv.contactName,
        contactEmail: adv.contactEmail,
        contactPhone: adv.contactPhone,
        isActive: adv.isActive,
        createdAt: adv.createdAt,
        updatedAt: adv.updatedAt,
        campaignCount: adv.campaignCount,
        clientId: adv.clientId || null,
        clientName: adv.clientName || null,
        // Billing info requires separate client fetch for full details
        billing: null,
        clientContact: null,
    }));
}

/**
 * Fetch single account with full client data enrichment
 */
export async function fetchAccount(id: number): Promise<Account> {
    // Fetch advertiser
    const advResponse = await apiRequest(`/api/admin/advertisers/${id}`, 'GET');
    const advData = await advResponse.json();

    if (!advData?.data?.advertiser) {
        throw new Error('Failed to fetch account');
    }

    const advertiser = advData.data.advertiser;

    // Build base account
    const account: Account = {
        id: advertiser.id,
        name: advertiser.name,
        address: advertiser.address,
        contactName: advertiser.contactName,
        contactEmail: advertiser.contactEmail,
        contactPhone: advertiser.contactPhone,
        isActive: advertiser.isActive,
        createdAt: advertiser.createdAt,
        updatedAt: advertiser.updatedAt,
        campaignCount: advertiser.campaignCount,
        clientId: advertiser.clientId || null,
        clientName: advertiser.clientName || null,
        billing: null,
        clientContact: null,
    };

    // If there's a linked client, fetch full client data for billing info
    if (advertiser.clientId) {
        try {
            const clientResponse = await apiRequest(`/api/clients/${advertiser.clientId}`, 'GET');
            const clientData = await clientResponse.json();

            if (clientData?.data?.client) {
                const client = clientData.data.client;
                account.clientName = client.name;
                account.billing = {
                    billingName: client.billingName || null,
                    billingAddress: client.billingAddress || null,
                    vatNumber: client.vatNumber || null,
                    paymentTerms: client.paymentTerms || null,
                };
                account.clientContact = {
                    name: client.primaryContactName || null,
                    email: client.primaryContactEmail || null,
                    phone: client.primaryContactPhone || null,
                };
            }
        } catch (error) {
            // Client fetch failed - continue without billing info
            console.warn('Failed to fetch client data for account:', error);
        }
    }

    return account;
}

/**
 * Create new account (creates advertiser, optionally links to client)
 */
export async function createAccount(data: AccountInput): Promise<Account> {
    const response = await apiRequest('/api/admin/advertisers', 'POST', data);
    const jsonData = await response.json();

    if (!jsonData?.data?.advertiser) {
        throw new Error(jsonData?.error || 'Failed to create account');
    }

    const adv = jsonData.data.advertiser;
    return {
        id: adv.id,
        name: adv.name,
        address: adv.address,
        contactName: adv.contactName,
        contactEmail: adv.contactEmail,
        contactPhone: adv.contactPhone,
        isActive: adv.isActive,
        createdAt: adv.createdAt,
        updatedAt: adv.updatedAt,
        clientId: adv.clientId || null,
        clientName: adv.clientName || null,
        billing: null,
        clientContact: null,
    };
}

/**
 * Update account (updates advertiser)
 */
export async function updateAccount(
    id: number,
    data: Partial<AccountInput>
): Promise<Account> {
    const response = await apiRequest(`/api/admin/advertisers/${id}`, 'PUT', data);
    const jsonData = await response.json();

    if (!jsonData?.data?.advertiser) {
        throw new Error(jsonData?.error || 'Failed to update account');
    }

    const adv = jsonData.data.advertiser;
    return {
        id: adv.id,
        name: adv.name,
        address: adv.address,
        contactName: adv.contactName,
        contactEmail: adv.contactEmail,
        contactPhone: adv.contactPhone,
        isActive: adv.isActive,
        createdAt: adv.createdAt,
        updatedAt: adv.updatedAt,
        clientId: adv.clientId || null,
        clientName: adv.clientName || null,
        billing: null,
        clientContact: null,
    };
}

/**
 * Delete account (deletes advertiser)
 */
export async function deleteAccount(id: number): Promise<void> {
    const response = await apiRequest(`/api/admin/advertisers/${id}`, 'DELETE');
    const jsonData = await response.json();

    if (!jsonData?.success) {
        throw new Error(jsonData?.error || 'Failed to delete account');
    }
}
